import { i18n } from '@/shared/i18n'

/**
 * Datas chegam em ISO 8601 (UTC) e são exibidas no fuso de São Paulo, no
 * idioma ativo do painel.
 */
const TIME_ZONE = 'America/Sao_Paulo'

/** Texto para valores ausentes nas tabelas e detalhes. */
export const EMPTY_VALUE = '—'

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE

  return new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: TIME_ZONE,
  }).format(new Date(value))
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE

  // Datas puras ("2026-10-06") não têm fuso: exibe como estão.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value)

  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeZone: TIME_ZONE }).format(date)
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(i18n.language, options).format(value)
}

/** Valor em centavos (como a API envia) formatado em reais. */
export function formatMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return EMPTY_VALUE

  return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' }).format(cents / 100)
}

/** Texto digitado ("1.234,56", "1234.5", "12") para centavos; null se vazio ou inválido. */
export function parseMoney(value: string): number | null {
  const clean = value.replace(/[^\d,.-]/g, '').trim()
  if (clean === '') return null

  const normalized = clean.includes(',') ? clean.replace(/\./g, '').replace(',', '.') : clean
  const amount = Number(normalized)

  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null
}

/** Centavos para o texto editável de um campo de valor ("1234,56"). */
export function moneyInput(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? '' : (cents / 100).toFixed(2).replace('.', ',')
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Data/hora de parede em São Paulo, no formato do `<input type="datetime-local">`. */
function wallClockParts(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  return Object.fromEntries(parts.map((part) => [part.type, part.value]))
}

/** ISO 8601 (UTC) → valor de `datetime-local` no fuso de São Paulo ("" quando vazio). */
export function toLocalDateTime(value: string | null | undefined): string {
  if (!value) return ''
  const p = wallClockParts(new Date(value))

  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
}

/** Valor de `datetime-local` (horário de São Paulo) → ISO 8601 em UTC (null quando vazio). */
export function fromLocalDateTime(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null

  const [, year, month, day, hour, minute] = match.map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ]
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const p = wallClockParts(new Date(guess))
  const offset = Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!) - guess

  return new Date(guess - offset).toISOString()
}
